/**
 * Pure rules for how the model optimizer describes its result: when the shown
 * result is stale, and how the fix list is titled. Kept free of DOM and of
 * value imports from the library so it can be unit-tested on its own
 * (test/vitest/tools-app/).
 */
import type { AxisConversion, MaterialOverride, OptimizationProfile } from 'trace-viewer/model-utils';

/** The settings that shape the optimized output. */
export interface OptimizeSettings {
  selectedProfile: OptimizationProfile;
  axisConversion: AxisConversion;
  scaleFactor: number;
  materialOverrides: Record<string, MaterialOverride>;
}

/** Everything that shapes the optimized output. Equal keys ⇒ same result. */
export function settingsKey(s: OptimizeSettings): string {
  return JSON.stringify([s.selectedProfile, s.axisConversion, s.scaleFactor, s.materialOverrides]);
}

/** True when an output exists but was made with different settings than now. */
export function isStale(outputKey: string | null, hasOutput: boolean, current: OptimizeSettings): boolean {
  return hasOutput && outputKey !== settingsKey(current);
}

/**
 * Title and lede for the fix list. While settings are unapplied, the list
 * judges the LAST result against the NEW targets — say so, rather than blaming
 * the optimizer for it.
 */
export function fixListHeading(opts: { optimized: boolean; dirty: boolean; count: number }): { title: string; lede: string } {
  const { optimized, dirty, count } = opts;
  const lede = dirty
    ? 'Based on the last result. Apply your changes to check again.'
    : optimized
      ? 'Optimizing couldn’t fix these on its own. Try a smaller quality or the settings under Adjust, or fix them in your 3D app.'
      : '';
  const title = optimized && count > 0 && !dirty ? 'Still needs attention' : 'What to fix';
  return { title, lede };
}
